import { ScrollNavigationTargetItem } from '@app/src/context/ScrollNavigationTargetItem'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import { br } from '@app/src/utils/translationMarkup'
import type { TypographyProps } from '@mui/material'
import { Box, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'

interface StepSectionProps {
  sectionId: string
  title: string
  children: ReactNode
  variant?: TypographyProps['variant']
}

const StepSection: React.FC<StepSectionProps> = ({ title, children, sectionId, variant = 'h3' }) => (
  <ScrollNavigationTargetItem id={sectionId}>
    <Stack spacing={1}>
      <Typography variant={variant} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </ScrollNavigationTargetItem>
)

function TopicalRiskMethodologyContent(): React.ReactElement {
  const { formatMessage } = useIntl()
  const providerType = useCurrentProviderType()

  return (
    <Stack gap={4} direction="column" borderRadius={2}>
      <StepSection
        sectionId="riskMethodology"
        variant="h1"
        title={formatMessage({ id: 'topicalRisk.methodology.riskMethodology' })}
      >
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.riskMethodologyContent.content1' }, { supplier: providerType })}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.riskMethodologyContent.content2' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.riskMethodologyContent.content3' }, { supplier: providerType })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.riskMethodologyContent.content4' },
            { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.riskMethodologyContent.content5' },
            { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
      </StepSection>
      <StepSection sectionId="howItWorks" title={formatMessage({ id: 'topicalRisk.methodology.howItWorks' })}>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.howItWorksContent.content1' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.howItWorksContent.content2' },
            { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.howItWorksContent.content3' },
            { supplier: providerType, b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
      </StepSection>
      <StepSection sectionId="topicRisk" title={formatMessage({ id: 'topicalRisk.methodology.topicRisk' })}>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.topicRiskContent.content1' })}
        </Typography>
        <Typography variant="h5" pt={2}>
          {formatMessage({ id: 'topicalRisk.methodology.topicRiskContent.content2.title' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.topicRiskContent.content2.content1' },
            { supplier: providerType, b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
        <Typography variant="h5" pt={2}>
          {formatMessage({ id: 'topicalRisk.methodology.topicRiskContent.content3.title' }, { supplier: providerType })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.topicRiskContent.content3.content1' },
            { supplier: providerType },
          )}
        </Typography>
        <Typography variant="h5" pt={2}>
          {formatMessage({ id: 'topicalRisk.methodology.topicRiskContent.content4.title' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.topicRiskContent.content4.content1' },
            { supplier: providerType },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.topicRiskContent.content4.aiScreening' },
            { supplier: providerType },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.topicRiskContent.content4.content2' })}
        </Typography>
        <Box
          component="img"
          src="/img/supplier-risk-matrix/level5SupplierRiskMatrix2026.png"
          alt="Level 5 Supplier Risk Matrix"
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 1,
            objectFit: 'contain',
          }}
        />
        <Box
          component="img"
          src="/img/supplier-risk-matrix/level3SupplierRiskMatrix2026.png"
          alt="Level 3 Supplier Risk Matrix"
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: 1,
            objectFit: 'contain',
          }}
        />
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.topicRiskContent.content4.content3' },
            {
              supplier: providerType,
              b: (chunks: React.ReactNode) => <b>{chunks}</b>,
              i: (chunks: React.ReactNode) => <i>{chunks}</i>,
              br: br,
            },
          )}
        </Typography>
      </StepSection>
      <StepSection sectionId="flags" title={formatMessage({ id: 'topicalRisk.methodology.flags' })}>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.flagsContent.content1' }, { supplier: providerType })}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.flagsContent.content2' }, { supplier: providerType })}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.flagsContent.content3' })}
        </Typography>
      </StepSection>
      <StepSection sectionId="overallRisk" title={formatMessage({ id: 'topicalRisk.methodology.overallRisk' })}>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.overallRiskContent.content1' }, { supplier: providerType })}
        </Typography>
      </StepSection>
      <StepSection
        sectionId="adaptToYourOrganization"
        title={formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganization' })}
      >
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content1' })}
        </Typography>
        <Typography variant="h5" pt={2}>
          {formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content2.title' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content2.content1' },
            { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content2.content2' },
            { supplier: providerType },
          )}
        </Typography>
        <Typography variant="h5" pt={2}>
          {formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content3.title' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content3.content1' },
            { supplier: providerType },
          )}
        </Typography>
        <Typography variant="body1">
          {formatMessage({ id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content3.content2' })}
        </Typography>
        <Typography variant="body1">
          {formatMessage(
            { id: 'topicalRisk.methodology.adaptToYourOrganizationContent.content3.content3' },
            { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
          )}
        </Typography>
      </StepSection>
    </Stack>
  )
}

export default TopicalRiskMethodologyContent
